/**
 * Utility function to get the correct media URL
 * Converts relative URLs to absolute URLs pointing to the backend
 * 
 * Works across all environments:
 * - Local: Uses proxy when VITE_APP_API_URL is not set
 * - Staging: Uses VITE_APP_API_URL from environment
 * - Production: Uses VITE_APP_API_URL from environment
 * 
 * @param {string|null|undefined} url - The media URL (can be relative or absolute)
 * @returns {string|null} - The processed URL or null if invalid
 */
export function getMediaUrl(url) {
  // Return null if url is null, undefined, empty string, or not a string
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return null;
  }
  
  // Trim whitespace
  const trimmedUrl = url.trim();

  // If app is served over HTTPS, coerce http:// media URLs to https://
  const coerceHttps = (absoluteUrl) => {
    if (
      typeof window !== 'undefined' &&
      window.location?.protocol === 'https:' &&
      absoluteUrl.startsWith('http://')
    ) {
      return absoluteUrl.replace(/^http:\/\//, 'https://');
    }
    return absoluteUrl;
  };
  
  // Get base URL for duplication check
  const apiUrl = import.meta.env.VITE_APP_API_URL;
  const cleanBaseUrl = apiUrl ? apiUrl.trim().replace(/\/+$/, '') : null;
  
  // First, check if URL is already duplicated (contains base URL twice)
  // This handles cases like: https://staging.meducate.digitalhttps//staging.meducate.digital/media/...
  if (cleanBaseUrl && trimmedUrl.includes(cleanBaseUrl)) {
    // Count how many times the base URL appears
    const baseUrlCount = (trimmedUrl.match(new RegExp(cleanBaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
    
    // If base URL appears more than once, it's duplicated - extract the first valid URL
    if (baseUrlCount > 1 && (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://'))) {
      // Extract the first valid URL pattern (stops at the second occurrence of base URL)
      const firstBaseUrlIndex = trimmedUrl.indexOf(cleanBaseUrl);
      const secondBaseUrlIndex = trimmedUrl.indexOf(cleanBaseUrl, firstBaseUrlIndex + cleanBaseUrl.length);
      
      if (secondBaseUrlIndex > 0) {
        // Extract URL up to the start of the second base URL occurrence
        const extractedUrl = trimmedUrl.substring(0, secondBaseUrlIndex);
        // Clean up any trailing invalid characters
        return coerceHttps(extractedUrl.replace(/[^/\w\-.:]+$/, ''));
      }
      
      // Fallback: try to extract first valid URL using regex
      const urlMatch = trimmedUrl.match(/^(https?:\/\/[^\s]+?)(?=https?:\/\/|$)/);
      if (urlMatch) {
        return coerceHttps(urlMatch[1]);
      }
    }
    
    // If it's already a full URL with the base URL (but not duplicated), return as-is
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return coerceHttps(trimmedUrl);
    }
  }
  
  // Check if already an absolute URL (http/https) - be more robust
  // Handle cases where URL might be malformed or have protocol variations
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return coerceHttps(trimmedUrl);
  }
  
  // Also check for URLs that might be absolute but missing protocol (edge case)
  // This shouldn't happen, but handle it defensively
  if (trimmedUrl.includes('://') && (trimmedUrl.includes('http') || trimmedUrl.includes('https'))) {
    // Try to extract and return a valid URL
    const match = trimmedUrl.match(/https?:\/\/[^\s]+/);
    if (match) {
      return match[0];
    }
  }
  
  // Helper to clean base URL (remove trailing slash)
  const getBaseUrl = () => {
    if (!apiUrl || typeof apiUrl !== 'string' || apiUrl.trim() === '') {
      return null;
    }
    return apiUrl.trim().replace(/\/+$/, ''); // Remove one or more trailing slashes
  };
  
  const baseUrl = getBaseUrl();
  
  // If it's a relative URL starting with /media/ or /static/
  if (trimmedUrl.startsWith('/media/') || trimmedUrl.startsWith('/static/')) {
    // If API URL is not set, use relative URL (works with proxy in development)
    if (!baseUrl) {
      return trimmedUrl;
    }
    
    // Construct full URL: baseUrl + /media/path
    return `${baseUrl}${trimmedUrl}`;
  }
  
  // If it's a relative URL without leading slash, prepend /media/
  // This handles cases like "institutions/logos/logo.png"
  if (!trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('http')) {
    // If API URL is not set, use relative URL with /media/ prefix
    if (!baseUrl) {
      return `/media/${trimmedUrl}`;
    }
    
    // Construct full URL: baseUrl + /media/ + path
    return `${baseUrl}/media/${trimmedUrl}`;
  }
  
  // If it starts with / but not /media/ or /static/, assume it's a backend path
  // This handles cases like "/api/some-endpoint" or other backend paths
  if (trimmedUrl.startsWith('/')) {
    // If API URL is not set, return as relative (works with proxy)
    if (!baseUrl) {
      return trimmedUrl;
    }
    
    // Construct full URL
    return `${baseUrl}${trimmedUrl}`;
  }
  
  // Return as is if it doesn't match expected patterns (shouldn't happen, but safe fallback)
  return trimmedUrl;
}

